import { Typography } from "@mui/material";
import { match } from "ts-pattern";
import type { ReadonlyDeep } from "type-fest";

import type {
  S132CurrentStatus,
  S132Duty,
  S132Outcome,
  S132Period,
  S132PeriodStatus,
} from "@thalamos/aspire-common";
import {
  MhaStatus,
  S132Outcome as Outcome,
  S132_DUTY_FOR,
  S132WithdrawalReason,
} from "@thalamos/aspire-common";

type Tone = "positive" | "pending" | "muted";

/** Theme status colours, so these pills read like eMHA's other status pills. */
const fillByTone: Record<Tone, string> = {
  positive: "statuses.positive",
  pending: "statuses.pending",
  muted: "common.lightGray",
};

export const s132StatusText = ({
  status,
  attemptCount,
}: Readonly<Pick<S132CurrentStatus, "status" | "attemptCount">>): string =>
  match(status)
    .with("notRecorded", () => "Not recorded")
    .with("notYetUnderstood", () => `Attempted (${String(attemptCount)})`)
    .with("understood", () => "Understood")
    .with("notTracked", () => "Not tracked")
    .exhaustive();

export const s132PeriodStatusText = (
  period: ReadonlyDeep<Pick<S132Period, "status" | "attemptIds">>,
): string =>
  s132StatusText({
    status: period.status,
    attemptCount: period.attemptIds.length,
  });

const toneForStatus = (status: S132PeriodStatus): Tone =>
  match(status)
    .with("notRecorded", (): Tone => "muted")
    .with("notYetUnderstood", (): Tone => "pending")
    .with("understood", (): Tone => "positive")
    .with("notTracked", (): Tone => "muted")
    .exhaustive();

/** A theme palette path, such as "statuses.pending", which resolves only inside an `sx` prop. */
export const s132StatusFill = (status: S132PeriodStatus): string =>
  fillByTone[toneForStatus(status)];

export const s132OutcomeText = (outcome: S132Outcome): string =>
  match(outcome)
    .with(Outcome.Understood, () => "Understood")
    .with(Outcome.NotUnderstood, () => "Did not understand")
    .with(Outcome.UnableToAssess, () => "Not able to assess")
    .exhaustive();

const toneForOutcome = (outcome: S132Outcome): Tone =>
  match(outcome)
    .with(Outcome.Understood, (): Tone => "positive")
    .with(Outcome.NotUnderstood, (): Tone => "pending")
    .with(Outcome.UnableToAssess, (): Tone => "pending")
    .exhaustive();

export const s132WithdrawalReasonText = (reason: S132WithdrawalReason) =>
  match(reason)
    .with(
      S132WithdrawalReason.WrongPatient,
      () => "Recorded against the wrong patient",
    )
    .with(S132WithdrawalReason.Duplicate, () => "Recorded twice")
    .with(S132WithdrawalReason.Other, () => "Another reason")
    .exhaustive();

// Labels for values read back from audit rows, which may predate the current
// enums; an unrecognised value is shown as stored.
export const s132OutcomeLabel = (value: string): string =>
  (Object.values(Outcome) as string[]).includes(value)
    ? s132OutcomeText(value as S132Outcome)
    : value;

export const s132WithdrawalReasonLabel = (value: string): string =>
  (Object.values(S132WithdrawalReason) as string[]).includes(value)
    ? s132WithdrawalReasonText(value as S132WithdrawalReason)
    : value;

/** An empty list names Section 132. */
const dutiesNamed = (
  duties: readonly S132Duty[],
): "s132" | "s132A" | "both" => {
  const named = new Set(duties);
  if (!named.has("s132A")) return "s132";
  return named.has("s132") ? "both" : "s132A";
};

export const s132DutiesText = (duties: readonly S132Duty[]): string =>
  match(dutiesNamed(duties))
    .with("s132", () => "S132")
    .with("s132A", () => "S132A")
    .with("both", () => "S132 and S132A")
    .exhaustive();

export const s132DutiesProvision = (duties: readonly S132Duty[]): string =>
  match(dutiesNamed(duties))
    .with("s132", () => "Section 132")
    .with("s132A", () => "Section 132A")
    .with("both", () => "Section 132 and 132A")
    .exhaustive();

export const s132DutyText = (duty: S132Duty): string => s132DutiesText([duty]);

export const s132DutyProvision = (duty: S132Duty): string =>
  s132DutiesProvision([duty]);

const isMhaStatus = (value: string | null | undefined): value is MhaStatus =>
  (Object.values(MhaStatus) as (string | null | undefined)[]).includes(value);

/** The provision an audited section owes, or Section 132 where it is absent or unknown. */
export const s132ProvisionLabel = (
  section: string | null | undefined,
): string =>
  s132DutyProvision(
    (isMhaStatus(section) ? S132_DUTY_FOR[section] : null) ?? "s132",
  );

function Pill(props: { tone: Tone; label: string }) {
  return (
    <Typography
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "0.75rem",
        borderRadius: "6px",
        padding: "3px 8px",
        whiteSpace: "nowrap",
        backgroundColor: fillByTone[props.tone],
        color: "text.primary",
      }}
    >
      {props.label}
    </Typography>
  );
}

export function S132StatusPill(props: {
  status: S132PeriodStatus;
  label: string;
}) {
  return <Pill tone={toneForStatus(props.status)} label={props.label} />;
}

export function S132OutcomePill(props: { outcome: S132Outcome }) {
  return (
    <Pill
      tone={toneForOutcome(props.outcome)}
      label={s132OutcomeText(props.outcome)}
    />
  );
}

export function S132CorrectedPill() {
  return <Pill tone="muted" label="Amended" />;
}

export function S132WithdrawnPill() {
  return <Pill tone="muted" label="Deleted" />;
}
